import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { usePhoneTripSearch } from '../../contexts/PhoneTripSearchContext';
import { FILLS, FONTS, RADII, SIZING, STROKES } from '../../styles/tokens';
import { SEARCH_RESULTS_PATH, SearchTab, isStaysOnlySearch } from '../../utils/tripSearchResults';
import { DISCOVER_SHOW_PARAM, DiscoverShow, parseDiscoverShow } from '../discover/discoverShow';
import MobileTripSearch from '../search/MobileTripSearch';
import { getHomePath } from './navigationHelpers';

interface MobileAppBarProps {
  /** When true, the left slot shows a back button instead of the logo. */
  back?: boolean;
  /** Overrides the default back behaviour (navigate(-1)). */
  onBack?: () => void;
  /** Invoked when the hamburger / menu button is pressed. */
  onMenuClick?: () => void;
  /** Overrides the default search behaviour (navigate to /discover). */
  onSearchClick?: () => void;
}

/**
 * The list pages and stay search results that hang off Discover. Together with Discover itself
 * (see `searchTabFor`) they show the search pill in place of the Search icon, for the search tab
 * each belongs to.
 */
const SEARCH_TAB_BY_PATH: Readonly<Record<string, SearchTab>> = {
  '/packages': 'discover',
  '/accommodations': 'stays',
  '/services': 'services',
};
const SERVICE_CATEGORY_PATH_PREFIX = '/services/category/';
/** The tabs whose pages carry their search text in the URL. */
const URL_SEARCH_TABS: readonly SearchTab[] = ['stays', 'services'];

/** Discover's chip row filters the feed in place; the pill searches what the chip shows. */
const DISCOVER_PATHS: readonly string[] = ['/', '/discover'];
const SEARCH_TAB_BY_SHOW: Readonly<Partial<Record<DiscoverShow, SearchTab>>> = {
  stays: 'stays',
  services: 'services',
};

const searchTabFor = (pathname: string, search: string): SearchTab | null => {
  if (DISCOVER_PATHS.includes(pathname)) {
    return (
      SEARCH_TAB_BY_SHOW[parseDiscoverShow(new URLSearchParams(search).get(DISCOVER_SHOW_PARAM))] ??
      'discover'
    );
  }
  if (pathname === SEARCH_RESULTS_PATH) {
    return isStaysOnlySearch(new URLSearchParams(search)) ? 'stays' : 'discover';
  }
  if (pathname.startsWith(SERVICE_CATEGORY_PATH_PREFIX)) return 'services';
  return SEARCH_TAB_BY_PATH[pathname] ?? null;
};
const PILL_GAP_PX = 16;

const TILE_SIZE = 44;
const BAR_PADDING_PX = 16;
const CLUSTER_GAP_PX = 8;
/** Cart and Menu on Discover: the wider side of the bar, which sets the pill's inset. */
const ICON_CLUSTER_WIDTH_PX = 2 * TILE_SIZE + CLUSTER_GAP_PX;
/** Inset of the pill from each screen edge: equal on both sides, so it centres on the screen. */
const PILL_INSET_PX = BAR_PADDING_PX + ICON_CLUSTER_WIDTH_PX + PILL_GAP_PX;
const ICON_SIZE = 16;

// Shared 44×44 rounded surface tile (WCAG 2.5.8 tap target) used by the back,
// search, cart, and menu controls. Centralised so the four call sites stay in sync.
const tileStyle: React.CSSProperties = {
  position: 'relative',
  width: TILE_SIZE,
  height: TILE_SIZE,
  flexShrink: 0,
  borderRadius: RADII.iconBox,
  background: FILLS.surface,
  border: `1px solid ${STROKES.subtle}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  color: FILLS.textLight,
};

const MobileAppBar: React.FC<MobileAppBarProps> = ({
  back = false,
  onBack,
  onMenuClick,
  onSearchClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchTab = searchTabFor(location.pathname, location.search);
  const hasSearchPill = searchTab !== null;
  const { user } = useAuth();
  const { getItemCount } = useCart();
  const { openRef } = usePhoneTripSearch();
  const itemCount = getItemCount();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleSearch = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate('/discover');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: SIZING.mobileAppBarHeight,
        // Opaque, no blur: iOS Safari tints the status bar only from an opaque fixed edge bar.
        background: FILLS.page,
        borderBottom: `1px solid ${STROKES.subtle}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `0 ${BAR_PADDING_PX}px`,
        zIndex: 1200,
        fontFamily: FONTS.body,
      }}
    >
      {/* Left slot: back button or logo */}
      {back ? (
        <button type="button" aria-label="Go back" onClick={handleBack} style={tileStyle}>
          <svg
            width={ICON_SIZE}
            height={ICON_SIZE}
            viewBox="0 0 24 24"
            fill="none"
            stroke={FILLS.textLight}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      ) : (
        <Link
          to={getHomePath(user)}
          aria-label="Belhop home"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
        >
          <img
            src="/assets/belhoplogo-text.png"
            alt="Belhop"
            style={{ height: 26, width: 'auto', display: 'block' }}
          />
        </Link>
      )}

      {hasSearchPill && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: PILL_INSET_PX,
            right: PILL_INSET_PX,
            transform: 'translateY(-50%)',
          }}
        >
          <MobileTripSearch
            tab={searchTab}
            openRef={openRef}
            initialText={
              URL_SEARCH_TABS.includes(searchTab) || location.pathname === SEARCH_RESULTS_PATH
                ? (new URLSearchParams(location.search).get('search') ?? '')
                : ''
            }
          />
        </div>
      )}

      {/* Right cluster: search (except where the pill replaces it), cart, menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: CLUSTER_GAP_PX }}>
        {!hasSearchPill && (
          <button type="button" aria-label="Search" onClick={handleSearch} style={tileStyle}>
            <svg
              width={ICON_SIZE}
              height={ICON_SIZE}
              viewBox="0 0 24 24"
              fill="none"
              stroke={FILLS.textLight}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4-4" />
            </svg>
          </button>
        )}

        <Link to="/cart" aria-label="Cart" style={{ ...tileStyle, textDecoration: 'none' }}>
          <svg
            width={ICON_SIZE}
            height={ICON_SIZE}
            viewBox="0 0 24 24"
            fill="none"
            stroke={FILLS.textLight}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 3h2l3.6 7.6-1.4 2.5c-.1.2-.2.5-.2.7 0 .9.7 1.6 1.6 1.6h11.4" />
            <circle cx="9" cy="20" r="1.4" />
            <circle cx="18" cy="20" r="1.4" />
            <path d="M6 6h15l-2.5 8H8.5" />
          </svg>
          {itemCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -5,
                right: -5,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: RADII.pill,
                background: FILLS.gold,
                color: FILLS.page,
                fontFamily: FONTS.body,
                fontSize: 10,
                fontWeight: 700,
                lineHeight: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${FILLS.page}`,
              }}
            >
              {itemCount}
            </span>
          )}
        </Link>

        <button type="button" aria-label="Menu" onClick={onMenuClick} style={tileStyle}>
          <svg
            width={ICON_SIZE}
            height={ICON_SIZE}
            viewBox="0 0 24 24"
            fill="none"
            stroke={FILLS.textLight}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18" />
            <path d="M3 12h18" />
            <path d="M3 18h18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MobileAppBar;
